/**
 * Global API that allows external tools to subscribe to events dispatched within our app.
 *
 * Read more in our documentation:
 * https://confluence.dev.aws.singtel.com/display/LUX/Analytics
 */

/**
 * Patches the window.luxEvents that already exists on the page.
 *
 * We need to run this if we are on a legacy page.
 *
 * This allows us to be backwards compatible when we migrate components across
 * without affecting external systems using this API.
 */
const patchLuxEvents = function() {
  let origListenTo = window.luxEvents.listenTo;

  window.luxEvents.listeners = [];

  const newListenFn = function(widget, handler) {
    origListenTo(widget, handler);
    window.luxEvents.listeners.push({
      widget: widget,
      handler: handler
    });
  };

  window.luxEvents.listenTo = newListenFn;
  window.luxEvents.on = newListenFn;
};

/**
 * Creates the windows.luxEvents API.
 * luxEvents.listenTo() is the subscriber API.
 *
 * We run this on a non legacy page.
 */
const createLuxEventsApi = function() {
  window.luxEvents = window.luxEvents || {};
  window.luxEvents.listeners = [];
  window.luxEvents.listenTo = function(widget, handler) {
    window.luxEvents.listeners.push({
      widget: widget,
      handler: handler
    });
  };
};

/**
 * Set up LuxEvents API
 * We do this on page load and we need to determine if we are
 * on a legacy page or a non-legacy page.
 */
const setupLuxEvents = function() {
  // If on legacy page and luxEvents is ready to be patched, we patch
  // it up straight away. If luxEvents is not ready, we wait for
  // legacy LUX to finish loading for us to patch it up.
  if (window.patternLibraryBaseUrl && window.luxEvents) {
    patchLuxEvents();
  } else if (window.patternLibraryBaseUrl && !window.luxEvents) {
    window.jQuery(document).on('allWidgetsInitialized.lux', patchLuxEvents);
  } else {
    createLuxEventsApi();
  }
};

/**
 * Redux middleware to publish all actions to our luxEvents API.
 * If a subscriber is found, it will notify it.
 */
const analytics = store => next => action => {
  let actionType = action.type;
  let actionNamespace = '';
  let result = next(action);
  let nextState = store.getState();

  // luxEvents is not ready, no need to capture any analytics.
  // This can happen if we added LUX2 onto a LUX1 page and LUX1
  // is taking too long to load. We need to wait for LUX1 otherwise
  // there is a risk of LUX1 overriding LUX2.
  //
  // The limitation to this design is that we cannot publish redux actions
  // until LUX1 has told us it is ready. Which means we won't be able to
  // publish actions dispatched during componentDidMount because it happens
  // too early. The way around this is to patch up legacy LUX to work with
  // future versions.
  if (!window.luxEvents || typeof window.luxEvents.listeners === 'undefined') {
    return result;
  }

  // Some actions can be namespaced
  // If they are namespaced then the first part is the component name
  // eg non-namespaced: SHOPPING_CART_FETCH
  // eg namespaced #1: PickAndGoWidget/FETCH_DATA
  // eg namespaced #2: PickAndGoWidget1/FETCH_DATA
  if (actionType.indexOf('/') !== -1) {
    actionNamespace = actionType.split('/')[0];
    actionType = actionType.split('/')[1];
  } else {
    actionNamespace = '*';
  }

  // We want a copy of the namespace without the count at the end
  let namespaceWithoutInstanceCount = actionNamespace.replace(/\d/g, '');

  // Go through all luxEvent subscriptions
  window.luxEvents.listeners
    .filter(function(sub) {
      return sub.widget === namespaceWithoutInstanceCount;
    })
    .forEach(function(sub) {
      // For those listeners with a handler we will execute
      if (typeof sub.handler === 'function') {
        sub.handler({
          action: actionType,
          el: document.getElementById(actionNamespace),
          state: nextState[actionNamespace]
        });
      }
    });

  return result;
};

export { setupLuxEvents };
export default analytics;
